import { useTranslation } from "react-i18next";
import { Button } from "~/atoms/button";
import { type WorkflowStep, WorkflowStepStatuses } from "~/api/types";
import { ArrowBigUp, Play } from "lucide-react";
import { type DocumentDetailsRelease } from "~/api/types";
import {
  canManageArtifact,
  canPublishArtifact,
  getWorkflowStepControlPermissions,
} from "~/helpers/documentPermissions";
import { useWorkflowStepTransition } from "~/hooks/useWorkflowStepTransition";

import { WorkflowNextStepButton } from "./WorkflowNextStepButton";
import { WorkflowTransitionConfirmDialog } from "./WorkflowTransitionConfirmDialog";

type Props = {
  step: WorkflowStep;
  stepIndex: number;
  totalSteps: number;
  documentArtifactId: number;
  selectedRelease: DocumentDetailsRelease;
  isWorkflowStarted: boolean;
  children: React.ReactNode;
  onChange: () => void;
};

export function WorkflowOverviewStepControlContainer({
  step,
  stepIndex,
  totalSteps,
  documentArtifactId,
  selectedRelease,
  isWorkflowStarted,
  children,
  onChange,
}: Props) {
  const { t } = useTranslation();
  const canManage = canManageArtifact(selectedRelease);
  const {
    isSubmitting,
    confirmMessage,
    requestNextStep,
    moveForward,
    moveBackward,
    dismissConfirm,
  } = useWorkflowStepTransition({ documentArtifactId, onChange });

  const isActiveStep = step.status === WorkflowStepStatuses.active;
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;

  const { canStartWorkflow, canMoveBackward, canMoveForward, showControls } =
    getWorkflowStepControlPermissions({
      canManage,
      canPublish: canPublishArtifact(selectedRelease),
      isWorkflowStarted,
      isActiveStep,
      isFirstStep,
      isLastStep,
      artifactStatus: selectedRelease.artifact.status,
    });

  return (
    <>
      {showControls && canMoveBackward && (
        <div className="col-start-2 flex justify-center">
          <Button
            variant="outline"
            onClick={moveBackward}
            disabled={isSubmitting}
            className="min-w-48"
          >
            <ArrowBigUp />
            {t(($) => $.commons.workflow.previousStep)}
          </Button>
        </div>
      )}

      {showControls && canStartWorkflow && (
        <div className="col-start-2 flex justify-center">
          <Button
            variant="default"
            onClick={moveForward}
            disabled={isSubmitting}
            size="sm"
          >
            <Play />
            {t(($) => $.commons.workflow.startWorkflow)}
          </Button>
        </div>
      )}

      {children}

      {showControls && canMoveForward && (
        <div className="col-start-2 flex justify-center">
          <WorkflowNextStepButton
            step={step}
            isSubmitting={isSubmitting}
            onNextStep={() => requestNextStep(step)}
            className="min-w-48"
          />
        </div>
      )}

      <WorkflowTransitionConfirmDialog
        message={confirmMessage}
        onConfirm={moveForward}
        onDismiss={dismissConfirm}
      />
    </>
  );
}
